import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, Navigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import { useAuth } from '../context/AuthContext';
import { fetchWithMeta, formatCount } from '../lib/listQuery';
import {
  QrCode, Star, Clock, CheckCircle, Lock, Gift, ChevronDown,
  ChevronUp, ExternalLink, Wallet, ScanLine, Loader2, EyeOff
} from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';
import MembershipCard from '../components/membership/MembershipCard';
import GuidedTour from '../components/tour/GuidedTour';
import { canScanCards } from '../lib/membershipAccess';
import './QRWalletPage.css';

import { TIER_COLORS as tierColors } from '../config/tiers';


// Stuart, 29 Oct: a sponsor that typed the same sentence into the offer
// title, value and description showed it three times on one card. A line
// that repeats one already shown is left out (same words, any case/spacing).
const sameText = (a, b) => !!a && !!b
  && String(a).trim().replace(/\s+/g, ' ').toLowerCase() === String(b).trim().replace(/\s+/g, ' ').toLowerCase();

const TIER_ORDER = { platinum: 1, gold: 2, silver: 3, standard: 4 };

const QRWalletPage = () => {
  const { clubSlug } = useParams();
  const { user, getMembership, loading: authLoading } = useAuth();
  const membership = getMembership(clubSlug);
  // SB-7 (Stuart, 19 Sept): Wallet for everyone signed in. Real members get
  // their codes + card; non-members browse the same offers here — codes stay
  // personal to members.
  // B-66 (Stuart, 15 Oct): Marakin staff see it like everyone else. Staff
  // who are REAL members of this club (implicit:false) get their real wallet,
  // card and codes. Staff on the IMPLICIT platform membership get a preview:
  // the card and every offer's QR, labelled "Staff preview — not valid for
  // redemption". The server decides (sponsors.js GET /wallet sends
  // `staff_preview`, e.g. for a waiting or archived row of their own) and
  // every scan/redeem path refuses preview codes (lib/staffPreview.js).
  const isStaff = !!user?.isPlatformUser || !!membership?.implicit;
  const hasRealMembership = !!membership && !membership.implicit;
  // WT-3 (17 Oct): a sponsor login has no Wallet. The navbar hid it, but
  // /<club>/wallet still opened and "Get my QR code" claimed member codes,
  // its own offer's included. Sent to Scan below, as the hub does; the
  // server refuses its codes, wallet and card too (403 sponsor_account).
  const isSponsorLogin = membership?.role === 'sponsor';
  const qc = useQueryClient();
  const [expandedCode, setExpandedCode] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(null); // sponsor id of the open preview QR
  const [showHistory, setShowHistory] = useState(false);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [codeStatuses, setCodeStatuses] = useState({}); // { codeId: { status, discount_value, ... } }
  // WT-2: the code string just claimed with "Get my QR code", waiting for the
  // refetched wallet to list it under "Your codes" so it can open there.
  const [openWhenListed, setOpenWhenListed] = useState(null);
  const [scrollToCode, setScrollToCode] = useState(null); // code id to bring into view once open
  const pollIntervalRef = useRef(null);

  // Poll for status changes when a code is expanded
  useEffect(() => {
    if (!expandedCode || !clubSlug) {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      return;
    }

    const pollStatus = async () => {
      try {
        const res = await axios.get(`/clubs/${clubSlug}/sponsors/code/${expandedCode}/status`);
        setCodeStatuses(prev => ({ ...prev, [expandedCode]: res.data }));

        // If redeemed, stop polling and refresh wallet
        if (res.data.status === 'redeemed') {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          // Refresh wallet data after a brief delay so the UI can show the redeemed state
          setTimeout(() => qc.invalidateQueries({ queryKey: ['wallet', clubSlug] }), 2000);
        }
      } catch { /* silent fail on polling */ }
    };

    pollStatus(); // immediate first check
    pollIntervalRef.current = setInterval(pollStatus, 2500);

    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, [expandedCode, clubSlug, qc]);

  const { data: club } = useQuery({ queryKey: ['club', clubSlug], queryFn: () => axios.get(`/clubs/${clubSlug}`).then(r => r.data) });
  // B-24: redeemedCodes is capped server-side, so the heading needs the true
  // total from X-Total-Count — it used to render the page length and stick at
  // "Redeemed codes (20)" for the rest of the member's life.
  const { data: walletRes, isLoading, isError: walletError } = useQuery({
    queryKey: ['wallet', clubSlug],
    queryFn: () => fetchWithMeta(`/clubs/${clubSlug}/sponsors/wallet`),
    enabled: !!user && (hasRealMembership || isStaff) && !isSponsorLogin,
    staleTime: 15000,
  });
  const wallet = walletRes?.data;
  const redeemedTotal = walletRes?.total;
  // B-66: which view. Preview = staff without a real membership here (or the
  // server said so). Real wallet = a real member, staff or not.
  const staffPreview = isStaff && (!hasRealMembership || !!wallet?.staff_preview);
  const isMember = hasRealMembership && !staffPreview;
  const previewOffers = wallet?.staff_preview?.offers || [];
  const previewLabel = wallet?.staff_preview?.label || 'Staff preview — not valid for redemption';
  // Browse-only offers for signed-in non-members — the public list Sponsor
  // Offers uses (same query key, so the cache is shared).
  const { data: publicOffers = [], isLoading: publicLoading, isError: publicError } = useQuery({
    queryKey: ['sponsors', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/sponsors`).then(r => r.data),
    enabled: !!user && !isMember && !staffPreview && !isSponsorLogin,
  });

  const generateCodeMutation = useMutation({
    mutationFn: (sponsorId) => {
      setGeneratingFor(sponsorId);
      return axios.get(`/clubs/${clubSlug}/sponsors/${sponsorId}/code`);
    },
    // WT-2 (16 Oct walk-through): the claimed offer used to drop into "Your
    // codes" folded, so at the till the only QR on screen was the club card's.
    // Open the new code instead. Matched on the code string: a fresh claim's
    // reply spreads the sponsor row last, so its `id` is the SPONSOR's id,
    // while `code` is the same in both reply shapes and in the wallet list.
    onSuccess: (res) => {
      setOpenWhenListed(res?.data?.code || null);
      qc.invalidateQueries({ queryKey: ['wallet', clubSlug] });
      setGeneratingFor(null);
    },
    onError: () => setGeneratingFor(null)
  });

  const accentColor = club?.accent_color || '#C4943A';
  const activeCodes = wallet?.activeCodes || [];

  // WT-2: once the refetched wallet lists the new code, open it (which also
  // folds the club card, as tapping a code does) and bring it into view.
  useEffect(() => {
    if (!openWhenListed) return;
    const listed = activeCodes.find(c => c.code === openWhenListed);
    if (!listed) return;
    setOpenWhenListed(null);
    setExpandedCode(listed.id);
    setScrollToCode(listed.id);
  }, [openWhenListed, activeCodes]);
  useEffect(() => {
    if (!scrollToCode) return undefined;
    // Next frame: after the open card and the folded club card have laid out.
    const frame = requestAnimationFrame(() => {
      const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      document.getElementById(`wallet-code-${scrollToCode}`)?.scrollIntoView({ behavior: calm ? 'auto' : 'smooth', block: 'start' });
      setScrollToCode(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [scrollToCode]);
  // B-86: /wallet?offer=<sponsorId> (the hub's featured-sponsor ad) lands on
  // that sponsor. An unredeemed code of theirs opens, QR showing; otherwise
  // the offer's card is brought into view and briefly highlighted. Once.
  const [searchParams] = useSearchParams();
  const focusOffer = searchParams.get('offer');
  const focusDone = useRef(false);
  // QA: wait for the list this person actually sees. A member's wallet loads
  // after the hub has already cached the public list, which isn't shown to them.
  const showsWallet = hasRealMembership || isStaff;
  const offersReady = showsWallet ? (!!wallet && !isLoading) : publicOffers.length > 0;
  useEffect(() => {
    if (!focusOffer || focusDone.current || !offersReady) return undefined;
    const code = (wallet?.activeCodes || []).find(c => c.sponsor_id === focusOffer);
    if (code) { focusDone.current = true; setExpandedCode(code.id); setScrollToCode(code.id); return undefined; }
    const frame = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-sponsor-id="${CSS.escape(focusOffer)}"]`);
      if (!el) return;
      focusDone.current = true;
      const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: calm ? 'auto' : 'smooth', block: 'center' });
      el.classList.add('wallet-card-focus');
      setTimeout(() => el.classList.remove('wallet-card-focus'), 2400);
    });
    return () => cancelAnimationFrame(frame);
  }, [focusOffer, offersReady, wallet, publicOffers]);
  // QA r2 #1: a redeemed code drops out of activeCodes but `expandedCode`
  // still held its id, leaving the club card folded with no code open.
  const codeOpen = !!expandedCode && activeCodes.some(c => c.id === expandedCode);
  const redeemedCodes = wallet?.redeemedCodes || [];
  const availableOffers = wallet?.availableOffers || [];

  // WT-3: after every hook (Rules of Hooks, as ClubHub's sponsor redirect).
  if (isSponsorLogin) return <Navigate to={`/${clubSlug}/scan`} replace />;

  return (
    <div className="wallet-page" style={{ '--accent': accentColor }}>
      <Navbar club={club} />
      {/* Member tour hops here for its Wallet steps (never auto-starts). */}
      <GuidedTour club={club} surface="wallet" />

      {/* v35: "My QR Wallet" → "Wallet" — it now holds the club card too.
          WT-28: the subtitle clamps at two lines, and on a phone beside the
          Scan cards button "Sponsor offers for everyone at Testvale RF…" was
          cut off. Short enough now to fit; the club's name is in the navbar. */}
      <PageHeader
        icon={Wallet}
        title="Wallet"
        subtitle={membership?.holdsCard ? 'Your club card and sponsor offers' : 'Sponsor offers for everyone in the club'}
      >
        {/* B-46: the gate/bar scanner's door (was its own navbar link). */}
        {canScanCards(membership) && (
          <Link to={`/${clubSlug}/scan-cards`} className="btn btn-primary btn-sm" data-tour="wallet-scan-cards">
            <ScanLine size={14} /> Scan cards
          </Link>
        )}
      </PageHeader>

      {/* Not logged in. WT-1: not while a stored session is still loading
          (or waiting on signal) — that read as "you've been signed out". */}
      {!user && authLoading && <div className="page-loading"><div className="spinner" style={{ width: 40, height: 40 }} /></div>}
      {!user && !authLoading && (
        <div className="wallet-content">
          <div className="wallet-locked">
            <Lock size={40} style={{ color: accentColor }} />
            <h2>Login Required</h2>
            <p>Log in to see {club?.name ? `${club.name}'s` : 'the club\'s'} sponsor offers and your codes.</p>
            {/* WT-14: come back to this Wallet after signing in or signing
                up (UC-B5 state.from; Login/Register check it with
                safeReturnPath). It used to land on the hub. */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
              <Link to="/login" state={{ from: `/${clubSlug}/wallet` }} className="btn btn-primary">Login</Link>
              <Link to="/register" state={{ from: `/${clubSlug}/wallet` }} className="btn btn-secondary">Register</Link>
            </div>
          </div>
        </div>
      )}

      {/* B-66: Marakin staff without a real membership here — the card and
          every offer's QR as members see them, all marked as a preview. */}
      {user && staffPreview && (
        <div className="wallet-content">
          <div className="wallet-preview-banner" role="note">
            <EyeOff size={18} />
            <div>
              <strong>{previewLabel}</strong>
              <p>You're viewing as Marakin staff, so this is what members of {club?.name || 'the club'} see. Scanning these codes is refused and nothing is recorded. Impersonate a member to see their own Wallet.</p>
            </div>
          </div>

          <MembershipCard clubSlug={clubSlug} club={club} hidden={!!previewOpen} onShow={() => setPreviewOpen(null)} />

          {isLoading && <div className="page-loading" style={{ minHeight: '30vh' }}><div className="spinner" /></div>}

          {walletError && (
            <div className="wallet-empty">
              <h3>Couldn't load the offers</h3>
              <p>Check your connection and try again in a moment.</p>
            </div>
          )}

          {!isLoading && !walletError && previewOffers.length > 0 && (
            <div className="wallet-section">
              <h2 className="wallet-section-title">
                <Gift size={18} style={{ color: accentColor }} /> Sponsor offers ({previewOffers.length})
              </h2>
              <p className="wallet-section-sub">Local businesses backing {club?.name || 'the club'}. Members tap one to get their own code.</p>
              <div className="wallet-cards">
                {previewOffers.map(offer => (
                  <div key={offer.id} data-sponsor-id={offer.id} className="wallet-card card">
                    <div className="wallet-card-header" onClick={() => setPreviewOpen(previewOpen === offer.id ? null : offer.id)}>
                      <div className="wallet-card-sponsor">
                        <div className="wallet-sponsor-logo" style={{ background: `${tierColors[offer.tier]}15` }}>
                          {offer.logo_url
                            ? <img src={offer.logo_url} alt={offer.business_name} />
                            : <span>{offer.business_name.charAt(0)}</span>
                          }
                        </div>
                        <div>
                          <span className="badge" style={{ background: `${tierColors[offer.tier]}20`, color: tierColors[offer.tier], marginBottom: 4 }}>
                            {offer.tier}
                          </span>
                          {offer.featured && <span className="badge badge-gold">Featured</span>}
                          <h3>{offer.business_name}</h3>
                          {!sameText(offer.offer_title, offer.offer_value) && <span className="wallet-offer-title">{offer.offer_title}</span>}
                        </div>
                      </div>
                      <div className="wallet-card-right">
                        {offer.offer_value && (
                          <span className="wallet-offer-value" style={{ color: accentColor }}>{offer.offer_value}</span>
                        )}
                        {previewOpen === offer.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </div>
                    </div>
                    {previewOpen === offer.id && (
                      <div className="wallet-card-expanded">
                        <div className="wallet-qr-section">
                          <span className="wallet-preview-pill"><EyeOff size={12} /> {previewLabel}</span>
                          <img src={offer.qr_image} alt="Staff preview QR code — not valid" className="wallet-qr-image wallet-qr-preview" />
                          <div className="wallet-code-text">STAFF PREVIEW</div>
                          <p className="wallet-qr-instruction">
                            A member would show their own code at {offer.business_name}. This one is refused if scanned.
                          </p>
                        </div>
                        {offer.offer_description && !sameText(offer.offer_description, offer.offer_value) && !sameText(offer.offer_description, offer.offer_title) && <p className="wallet-offer-desc">{offer.offer_description}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isLoading && !walletError && wallet && previewOffers.length === 0 && (
            <div className="wallet-empty">
              <QrCode size={48} style={{ color: 'var(--text-muted)' }} />
              <h3>No sponsor offers yet</h3>
              <p>When {club?.name || 'the club'} adds sponsors, their offers appear here.</p>
            </div>
          )}
        </div>
      )}

      {/* SB-7: signed in but not a member — an empty card slot that says
          why, then the club's offers to browse (no codes). */}
      {user && !isMember && !staffPreview && (
        <div className="wallet-content">
          <div className="wallet-card-slot">
            <Lock size={22} style={{ color: accentColor }} />
            <p>Your club card and your own codes appear here once you've joined {club?.name || 'the club'}.</p>
            <Link to={`/${clubSlug}`} className="btn btn-primary btn-sm">Go to club hub</Link>
          </div>

          {publicLoading && <div className="page-loading" style={{ minHeight: '30vh' }}><div className="spinner" /></div>}

          {publicError && (
            <div className="wallet-empty">
              <h3>Couldn't load the offers</h3>
              <p>Check your connection and try again in a moment.</p>
            </div>
          )}

          {!publicLoading && !publicError && publicOffers.length > 0 && (
            <div className="wallet-section">
              <h2 className="wallet-section-title">
                <Gift size={18} style={{ color: accentColor }} /> Sponsor offers ({publicOffers.length})
              </h2>
              <p className="wallet-section-sub">Local businesses backing {club?.name || 'the club'}.</p>
              <div className="wallet-cards">
                {[...publicOffers]
                  .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || (TIER_ORDER[a.tier] || 4) - (TIER_ORDER[b.tier] || 4))
                  .map(offer => (
                  <div key={offer.id} data-sponsor-id={offer.id} className="wallet-card card">
                    <div className="wallet-card-header">
                      <div className="wallet-card-sponsor">
                        <div className="wallet-sponsor-logo" style={{ background: `${tierColors[offer.tier]}15` }}>
                          {offer.logo_url
                            ? <img src={offer.logo_url} alt={offer.business_name} />
                            : <span>{offer.business_name.charAt(0)}</span>
                          }
                        </div>
                        <div>
                          <span className="badge" style={{ background: `${tierColors[offer.tier]}20`, color: tierColors[offer.tier], marginBottom: 4 }}>
                            {offer.tier}
                          </span>
                          {offer.featured && <span className="badge badge-gold">Featured</span>}
                          <h3>{offer.business_name}</h3>
                          {!sameText(offer.offer_title, offer.offer_value) && <span className="wallet-offer-title">{offer.offer_title}</span>}
                        </div>
                      </div>
                      {offer.offer_value && (
                        <span className="wallet-offer-value" style={{ color: accentColor }}>{offer.offer_value}</span>
                      )}
                    </div>
                    {offer.offer_description && !sameText(offer.offer_description, offer.offer_value) && !sameText(offer.offer_description, offer.offer_title) && <p className="wallet-offer-desc">{offer.offer_description}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!publicLoading && !publicError && publicOffers.length === 0 && (
            <div className="wallet-empty">
              <QrCode size={48} style={{ color: 'var(--text-muted)' }} />
              <h3>No sponsor offers yet</h3>
              <p>When {club?.name || 'the club'} adds sponsors, their offers appear here.</p>
            </div>
          )}
        </div>
      )}

      {/* Wallet content */}
      {user && isMember && (
        <div className="wallet-content">
          {/* v35: the club membership card sits at the top — renders nothing
              for non-holders (no teaser, no locked card) or clubs with cards off. */}
          <MembershipCard clubSlug={clubSlug} club={club} hidden={codeOpen} onShow={() => setExpandedCode(null)} />

          {isLoading && <div className="page-loading" style={{ minHeight: '30vh' }}><div className="spinner" /></div>}

          {!isLoading && (
            <>
              {/* Active codes */}
              {activeCodes.length > 0 && (
                <div className="wallet-section" data-tour="wallet-codes">
                  <h2 className="wallet-section-title">
                    <QrCode size={18} style={{ color: accentColor }} /> Your codes ({activeCodes.length})
                  </h2>
                  <div className="wallet-cards">
                    {activeCodes.map(code => (
                      <div key={code.id} id={`wallet-code-${code.id}`} className="wallet-card card">
                        <div className="wallet-card-header" onClick={() => setExpandedCode(expandedCode === code.id ? null : code.id)}>
                          <div className="wallet-card-sponsor">
                            <div className="wallet-sponsor-logo" style={{ background: `${tierColors[code.tier]}15` }}>
                              {code.logo_url
                                ? <img src={code.logo_url} alt={code.business_name} />
                                : <span>{code.business_name.charAt(0)}</span>
                              }
                            </div>
                            <div>
                              <span className="badge" style={{ background: `${tierColors[code.tier]}20`, color: tierColors[code.tier], marginBottom: 4 }}>
                                {code.tier}
                              </span>
                              <h3>{code.business_name}</h3>
                              {!sameText(code.offer_title, code.offer_value) && <span className="wallet-offer-title">{code.offer_title}</span>}
                            </div>
                          </div>
                          <div className="wallet-card-right">
                            {code.offer_value && (
                              <span className="wallet-offer-value" style={{ color: accentColor }}>{code.offer_value}</span>
                            )}
                            {expandedCode === code.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                          </div>
                        </div>

                        {expandedCode === code.id && (() => {
                          const liveStatus = codeStatuses[code.id];
                          const isBeingScanned = liveStatus && ['pending_value', 'pending_confirm'].includes(liveStatus.status);
                          const isRedeemed = liveStatus && liveStatus.status === 'redeemed';

                          return (
                            <div className="wallet-card-expanded">
                              {/* Redeemed state — show discount confirmation */}
                              {isRedeemed && (
                                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                  <CheckCircle size={48} style={{ color: 'var(--success)', marginBottom: 12 }} />
                                  <h3 style={{ color: 'var(--success)', margin: '0 0 16px' }}>Discount Redeemed!</h3>
                                  <div className="scan-result-details" style={{ textAlign: 'left' }}>
                                    {liveStatus.discount_value && (
                                      <div className="scan-detail-row">
                                        <span>Discount Applied</span>
                                        <strong style={{ color: 'var(--success)' }}>−£{parseFloat(liveStatus.discount_value).toFixed(2)}</strong>
                                      </div>
                                    )}
                                    {liveStatus.net_value && (
                                      <div className="scan-detail-row">
                                        <span>You Pay</span>
                                        <strong style={{ fontSize: 18 }}>£{parseFloat(liveStatus.net_value).toFixed(2)}</strong>
                                      </div>
                                    )}
                                  </div>
                                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 12 }}>
                                    Redeemed {liveStatus.redeemed_at ? new Date(liveStatus.redeemed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'just now'}
                                  </p>
                                </div>
                              )}

                              {/* Being scanned — show live status */}
                              {isBeingScanned && !isRedeemed && (
                                <div style={{ textAlign: 'center', padding: '16px 0 8px', borderBottom: '1px solid var(--border)', marginBottom: 12 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--green)' }}>
                                    <Loader2 size={16} className="spin-slow" />
                                    <span style={{ fontWeight: 700, fontSize: 14 }}>
                                      {liveStatus.status === 'pending_value' ? 'Staff are processing your code...' : 'Confirming your discount...'}
                                    </span>
                                  </div>
                                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                                    Your discount will appear here once confirmed. If the staff
                                    member gets interrupted, your code unlocks again by itself
                                    after a few minutes.
                                  </p>
                                </div>
                              )}

                              {/* Normal QR display (hidden if redeemed) */}
                              {!isRedeemed && (
                                <>
                                  <div className="wallet-qr-section">
                                    <img src={code.qr_image} alt="QR Code" className="wallet-qr-image" />
                                    <div className="wallet-code-text">{code.code}</div>
                                    <p className="wallet-qr-instruction">
                                      Show this QR code at {code.business_name} to redeem your discount.
                                    </p>
                                  </div>

                                  {code.offer_description && !sameText(code.offer_description, code.offer_value) && !sameText(code.offer_description, code.offer_title) && (
                                    <p className="wallet-offer-desc">{code.offer_description}</p>
                                  )}

                                  <div className="wallet-code-meta">
                                    {code.expires_at && (
                                      <span className="wallet-expiry">
                                        <Clock size={13} />
                                        Expires {new Date(code.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    )}
                                    <span className="wallet-single-use">Single use only</span>
                                  </div>

                                  {code.website && (
                                    <a href={code.website} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ marginTop: 8 }}>
                                      <ExternalLink size={13} /> Visit {code.business_name}
                                    </a>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Available offers (no code yet) */}
              {availableOffers.length > 0 && (
                <div className="wallet-section" data-tour="wallet-offers">
                  <h2 className="wallet-section-title">
                    <Gift size={18} style={{ color: accentColor }} /> Sponsor offers ({availableOffers.length})
                  </h2>
                  <p className="wallet-section-sub">Local businesses backing {club?.name || 'the club'}. Tap one to get your own code — show it at the till.</p>
                  <div className="wallet-cards">
                    {availableOffers.map(offer => (
                      <div key={offer.id} data-sponsor-id={offer.id} className="wallet-card card wallet-card-available">
                        <div className="wallet-card-header">
                          <div className="wallet-card-sponsor">
                            <div className="wallet-sponsor-logo" style={{ background: `${tierColors[offer.tier]}15` }}>
                              {offer.logo_url
                                ? <img src={offer.logo_url} alt={offer.business_name} />
                                : <span>{offer.business_name.charAt(0)}</span>
                              }
                            </div>
                            <div>
                              <span className="badge" style={{ background: `${tierColors[offer.tier]}20`, color: tierColors[offer.tier], marginBottom: 4 }}>
                                {offer.tier}
                              </span>
                              {offer.featured && <span className="badge badge-gold">Featured</span>}
                              {offer.category && <span className="wallet-offer-cat">{offer.category}</span>}
                              <h3>{offer.business_name}</h3>
                              {!sameText(offer.offer_title, offer.offer_value) && <span className="wallet-offer-title">{offer.offer_title}</span>}
                            </div>
                          </div>
                          {offer.offer_value && (
                            <span className="wallet-offer-value" style={{ color: accentColor }}>{offer.offer_value}</span>
                          )}
                        </div>
                        {(() => {
                          const desc = sameText(offer.offer_description, offer.offer_value) || sameText(offer.offer_description, offer.offer_title)
                            ? '' : offer.offer_description;
                          if (!desc && !offer.description) return null;
                          return (
                            <p className="wallet-offer-desc">
                              {desc}
                              {offer.description && <span className="wallet-offer-about">{desc ? ' · ' : ''}{offer.description}</span>}
                            </p>
                          );
                        })()}
                        <button
                          className="btn btn-primary wallet-generate-btn"
                          style={{ background: accentColor }}
                          onClick={() => generateCodeMutation.mutate(offer.id)}
                          disabled={generatingFor === offer.id}
                        >
                          {generatingFor === offer.id
                            ? 'Generating...'
                            : <><QrCode size={15} /> Get my QR code</>
                          }
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {activeCodes.length === 0 && availableOffers.length === 0 && (
                <div className="wallet-empty">
                  <QrCode size={48} style={{ color: 'var(--text-muted)' }} />
                  <h3>No sponsor offers yet</h3>
                  <p>When {club?.name || 'the club'} adds sponsors, their offers appear here for everyone at the club.</p>
                </div>
              )}

              {/* Redeemed history */}
              {redeemedCodes.length > 0 && (
                <div className="wallet-section">
                  <button className="wallet-history-toggle" onClick={() => setShowHistory(!showHistory)}>
                    <CheckCircle size={16} style={{ color: 'var(--text-muted)' }} />
                    Used codes ({formatCount(redeemedCodes.length, redeemedTotal)})
                    {showHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {showHistory && (
                    <div className="wallet-history">
                      {redeemedCodes.map(code => (
                        <div key={code.id} className="wallet-history-item">
                          <CheckCircle size={14} style={{ color: 'var(--success)' }} />
                          <div className="wallet-history-info">
                            <strong>{code.business_name}</strong>
                            <span>{sameText(code.offer_title, code.offer_value) ? code.offer_value : `${code.offer_title} · ${code.offer_value}`}</span>
                          </div>
                          <span className="wallet-history-date">
                            {new Date(code.redeemed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default QRWalletPage;
